// Step 3's currently achievable size: the lower of the structural and grid limits, the
// structural report's kWp span and the grid offer's kWac read in both units at the
// building's DC/AC ratio (docs/Y2026-Q4/design-achievable-size-limits.md).
import type { AmazonBuilding } from "../../api/hooks/useAmazonOverview";
import type { Tone } from "../../utils/sizeGap";
import { capitalised, offeredKwAc } from "./cells";
import { one, ppaShortfall, sizeText, type Span, spanText } from "./sizes";

/** What one limit says: it does not apply, nothing is in yet, it binds with no figure, or
 * its figure. */
type Limit<T> =
  | { kind: "none" }
  | { kind: "missing" }
  | { kind: "unsized" }
  | { kind: "sized"; size: T };

type Unsized = Exclude<Limit<unknown>["kind"], "sized">;

export interface AchievableSize {
  kwp: Span | null;
  kwAc: Span | null;
  caption: string;
  /** The structural report finds part of the roof viable, neither none nor all of it, so
   * the PV redesign sets the figure. */
  redesign: boolean;
  tone: Tone | undefined;
}

const sized = <T,>(size: T): Limit<T> => ({ kind: "sized", size });

/** The structural report's viable share of the max-out design in kWp, as the structural
 * cell reads it; else the structural kWp Building readiness records. */
const structureOf = (b: AmazonBuilding): Limit<Span> => {
  const s = b.steps.structural;
  if (!s || s.state === "not_applicable") return { kind: "none" };
  // A decision not to proceed: the roof takes no PV.
  if (s.kwp === 0) return sized(one(0));
  const viable = s.state === "done" ? s.roof_viable : null;
  if (viable?.max_pct === 0) return sized(one(0));
  if (viable?.kwp) return sized(viable.kwp);
  if (s.kwp != null) return sized(one(s.kwp));
  // A report relied on that gives no size, or a limit Building readiness has not sized.
  return viable || s.state === "done" || s.note ? { kind: "unsized" } : { kind: "missing" };
};

/** The kWac the grid offer in hand allows, as the grid offer cell reads it. */
const gridOf = (b: AmazonBuilding): Limit<number> => {
  const offer = b.steps.gridoffer;
  if (!offer || offer.state === "not_applicable") return { kind: "none" };
  if (b.steps.gridacc?.state !== "done" && offer.state !== "done") return { kind: "missing" };
  const kwAc = offeredKwAc(b);
  return kwAc == null ? { kind: "unsized" } : sized(kwAc);
};

/** The ratio in the order the platform takes it (achievable_kwp.dc_ac_ratio): the max-out
 * design's, else the PPA's as the page shows it, else the proposed figures'. */
const ratioOf = (b: AmazonBuilding): number | null => {
  const r = b.dc_ac_ratio;
  if (r?.basis === "max_out") return r.ratio;
  if (b.committed_kwp && b.committed_kw_ac) return b.committed_kwp / b.committed_kw_ac;
  return r?.ratio ?? null;
};

const STRUCTURE_SAYS: Record<Unsized, string> = {
  none: "no structural limit applies",
  missing: "no structural report yet",
  unsized: "the structural limit is not sized yet",
};

const GRID_SAYS: Record<Unsized, string> = {
  none: "no grid limit applies",
  missing: "no grid offer yet",
  unsized: "the grid offer's size is not recorded",
};

/** What a limit with no figure says, "" for one with a figure. */
const says = <T,>(phrases: Record<Unsized, string>, limit: Limit<T>): string =>
  limit.kind === "sized" ? "" : phrases[limit.kind];

/** Red or amber where even the highest figure falls short of the PPA, amber where only the
 * lowest does; on whichever of kWp and kWac falls further. */
const toneOf = (b: AmazonBuilding, kwp: Span | null, kwAc: Span | null): Tone | undefined => {
  const tones = (
    [
      [kwp, b.committed_kwp],
      [kwAc, b.committed_kw_ac],
    ] as const
  ).map(([span, ppa]) =>
    span == null
      ? undefined
      : (ppaShortfall(span.max, ppa) ?? (ppaShortfall(span.min, ppa) ? "amber" : undefined)),
  );
  return tones.includes("red") ? "red" : tones.includes("amber") ? "amber" : undefined;
};

/** How far apart, in kWp, the grid's and the structure's figures may sit and still be the
 * same size: the two are each rounded to the kWp, from a ratio taken to many places. */
const AGREE_KWP = 1;

interface FromLimits {
  kwp: Span | null;
  kwAc: Span | null;
  caption: string;
}

/** The size the two limits allow and what they say; null where neither gives a figure. */
const fromLimits = (b: AmazonBuilding): FromLimits | null => {
  const structure = structureOf(b);
  const grid = gridOf(b);
  const r = ratioOf(b);
  if (structure.kind !== "sized" && grid.kind !== "sized") return null;
  if (structure.kind === "sized" && structure.size.max === 0) {
    return { kwp: one(0), kwAc: one(0), caption: "The structural assessment allows no PV" };
  }
  const works = b.steps.structural?.roof_viable?.after_works ? " after works" : "";
  const s = structure.kind === "sized" ? structure.size : null;
  const g = grid.kind === "sized" ? grid.size : null;
  const sText = s && `${spanText(s, "kWp")}${works}`;
  const gText = g != null && sizeText(g, "kWac");
  if (!r) {
    return {
      kwp: s,
      kwAc: g == null ? null : one(g),
      caption:
        s && g != null
          ? `Structure allows ${sText} and the grid offer ${gText}; no DC/AC ratio on record to say which is lower`
          : s
            ? `Structure allows at most ${sText}. ${capitalised(says(GRID_SAYS, grid))}`
            : `The grid offer allows at most ${gText}. ${capitalised(says(STRUCTURE_SAYS, structure))}`,
    };
  }
  // At each end of the structural span (no end where structure gives no figure), the grid's
  // figure where it is the lower, else the structure's.
  const gKwp = g == null ? Infinity : Math.round(g * r);
  const end = (x: number): [number, number] =>
    gKwp <= x ? [gKwp, g ?? 0] : [x, Math.min(Math.round(x / r), g ?? Infinity)];
  const [loKwp, loKwAc] = end(s?.min ?? Infinity);
  const [hiKwp, hiKwAc] = end(s?.max ?? Infinity);
  const kwp = { min: loKwp, max: hiKwp };
  const kwAc = { min: loKwAc, max: hiKwAc };
  if (s && g != null) {
    return {
      kwp,
      kwAc,
      caption:
        s.min === s.max && Math.abs(gKwp - s.max) <= AGREE_KWP
          ? `Grid and structure agree: the offer's ${gText} and the structure's ${sText}`
          : gKwp <= s.min
          ? `Grid limits it: the offer's ${gText}. Structure allows ${sText}`
          : gKwp >= s.max
            ? `Structure limits it to ${sText}. The grid offer allows ${gText}`
            : `Structure allows ${sText}; the grid offer's ${gText} caps it`,
    };
  }
  // One limit with a figure: it limits the size only where the other does not apply.
  const caption = s
    ? grid.kind === "none"
      ? `Structure limits it to ${sText}. No grid limit applies`
      : `Structure allows at most ${sText}. ${capitalised(says(GRID_SAYS, grid))}`
    : structure.kind === "none"
      ? `Grid limits it: the offer's ${gText}. No structural limit applies`
      : `The grid offer allows at most ${gText}. ${capitalised(says(STRUCTURE_SAYS, structure))}`;
  return { kwp, kwAc, caption };
};

/** The size the card shows and its caption: from the structural and grid limits where
 * either gives a figure, a unit written over on the page taking its written figure, else
 * as recorded. Installation time reads the top of its kWp (schedule.installLength). */
export const achievableSize = (b: AmazonBuilding): AchievableSize => {
  const writtenOver = b.achievable_written ?? { kwp: false, kwAc: false };
  const viable = b.steps.structural?.state === "done" ? b.steps.structural.roof_viable : null;
  const redesign =
    b.steps.structural?.kwp !== 0 && !!viable && viable.max_pct > 0 && viable.min_pct < 100;
  const limits = fromLimits(b);
  const recordedKwp = b.achievable_kwp == null ? null : one(b.achievable_kwp);
  const recordedKwAc = b.achievable_kw_ac == null ? null : one(b.achievable_kw_ac);
  const kwp = writtenOver.kwp || !limits ? recordedKwp : limits.kwp;
  const kwAc = writtenOver.kwAc || !limits ? recordedKwAc : limits.kwAc;
  const tone = toneOf(b, kwp, kwAc);
  const generic = tone
    ? "Below the PPA size: roof or grid limits it"
    : "Roof and grid allow the full size";
  const structure = structureOf(b);
  const grid = gridOf(b);
  const caption =
    kwp == null && kwAc == null
      ? "Not recorded yet"
      : writtenOver.kwp || writtenOver.kwAc
        ? generic
        : limits
          ? limits.caption
          : structure.kind === "none" && grid.kind === "none"
            ? generic
            : `The recorded size: ${says(STRUCTURE_SAYS, structure)}, ${says(GRID_SAYS, grid)}`;
  return { kwp, kwAc, caption, redesign, tone };
};
